import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import Select, { MultiValue } from "react-select";
import {
  FaChevronDown,
  FaChevronUp,
  FaEdit,
  FaImages,
  FaLayerGroup,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTag,
  FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import authHeader from "../services/auth-header";
import { API_URL_PRODUCTS, BASE_URL } from "../constants/index";
import { RootState } from "../store";

const API_URL_PRODUCT_CATEGORIES = `${BASE_URL}/product-categories`;
const API_URL_PRODUCT_VARIATIONS = `${BASE_URL}/product-variations`;
const API_URL_PRODUCT_VARIATION_VALUES = `${BASE_URL}/product-variation-values`;
const API_URL_PRODUCT_VARIATION_COMBINATIONS = `${BASE_URL}/product-variation-combinations`;
const API_URL_PRODUCT_VARIATION_COMBINATION_VALUES = `${BASE_URL}/product-variation-combination-values`;
const API_URL_PRODUCT_VARIATION_PHOTO_SETS = `${BASE_URL}/product-variation-photo-sets`;
const API_URL_PRODUCT_VARIATION_PHOTO_SET_VALUES = `${BASE_URL}/product-variation-photo-set-values`;
const API_URL_PRODUCT_VARIATION_PHOTO_SET_PHOTOS = `${BASE_URL}/product-variation-photo-set-photos`;
const API_URL_CATEGORIES = `${BASE_URL}/categories`;
const API_URL_VARIATIONS = `${BASE_URL}/variations`;

type AlertState = {
  variant: "success" | "danger" | "warning";
  message: string;
};

type ModalMode = "create" | "edit";

type PageResponse<T> = {
  content?: T[];
  totalPages?: number;
  totalElements?: number;
  number?: number;
  size?: number;
  message?: string;
};

type IdName = {
  id?: number;
  name?: string;
  description?: string;
  title?: string;
  value?: string;
  varCode?: string;
  [key: string]: any;
};

type Product = {
  id: number;
  sku?: string | null;
  name: string;
  description?: string | null;
  price: number;
  store?: IdName | null;
  storeId?: number | null;
  categories?: IdName[];
  variations?: IdName[];
  productCategories?: ProductCategory[];
  productVariations?: ProductVariation[];
  productVariationCombinations?: ProductVariationCombination[];
  productVariationPhotoSets?: ProductVariationPhotoSet[];
  [key: string]: any;
};

type ProductCategory = {
  id: number;
  product?: Product | IdName;
  category?: IdName;
  categoryId?: number;
  [key: string]: any;
};

type ProductVariation = {
  id: number;
  product?: Product | IdName;
  variation?: IdName;
  variationId?: number;
  values?: ProductVariationValue[];
  productVariationValues?: ProductVariationValue[];
  [key: string]: any;
};

type ProductVariationValue = {
  id: number;
  productVariation?: ProductVariation | IdName;
  productVariationId?: number;
  name?: string;
  value?: string;
  [key: string]: any;
};

type ProductVariationCombination = {
  id: number;
  product?: Product | IdName;
  productId?: number;
  varCode?: string;
  combinationKey?: string;
  combination_key?: string;
  price?: number;
  available?: number;
  values?: ProductVariationCombinationValue[];
  combinationValues?: ProductVariationCombinationValue[];
  productVariationCombinationValues?: ProductVariationCombinationValue[];
  [key: string]: any;
};

type ProductVariationCombinationValue = {
  id: number;
  productVariationCombination?: ProductVariationCombination | IdName;
  combinationId?: number;
  productVariationCombinationId?: number;
  productVariationValue?: ProductVariationValue;
  productVariationValueId?: number;
  [key: string]: any;
};

type ProductVariationPhotoSet = {
  id: number;
  productId?: number;
  combinationKey?: string;
  combination_key?: string;
  name?: string | null;
  priority?: number;
  active?: boolean;
  valueIds?: number[];
  values?: ProductVariationPhotoSetValue[];
  photos?: ProductVariationPhotoSetPhoto[];
  [key: string]: any;
};

type ProductVariationPhotoSetValue = {
  photoSetId?: number;
  productVariationValueId?: number;
  productVariationValue?: ProductVariationValue;
  [key: string]: any;
};

type ProductVariationPhotoSetPhoto = {
  id: number;
  photoSetId: number;
  objectKey?: string;
  originalFilename?: string;
  contentType?: string;
  sizeBytes?: number;
  altText?: string | null;
  position?: number;
  primary?: boolean;
  url?: string;
  [key: string]: any;
};

type ProductForm = {
  sku: string;
  name: string;
  description: string;
  categoryIds: number[];
  variationIds: number[];
};

type SelectOption = {
  value: number;
  label: string;
};

type VariationValueForm = {
  id?: number;
  productVariationId: number;
  name: string;
  value: string;
};

type CombinationForm = {
  id?: number;
  selectedValueIds: Record<number, number>;
  varCode: string;
  price: string;
  available: string;
};

type PhotoSetForm = {
  photoSetId?: number;
  combinationId?: number;
  availableValueIds: number[];
  selectedValueIds: number[];
  name: string;
  priority: string;
  active: boolean;
  files: File[];
  photos: ProductVariationPhotoSetPhoto[];
};

const emptyForm: ProductForm = {
  sku: "",
  name: "",
  description: "",
  categoryIds: [],
  variationIds: [],
};

const getPageItems = <T,>(data: PageResponse<T> | T[] | any): T[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const getItemLabel = (item?: IdName | null) => {
  if (!item) return "-";
  return item.name || item.title || item.value || item.description || item.varCode || `ID ${item.id ?? "-"}`;
};

const getRelationId = (relation: ProductCategory | ProductVariation, key: "category" | "variation") => {
  const direct = relation[`${key}Id` as keyof typeof relation];
  if (typeof direct === "number") return direct;
  const nested = relation[key] as IdName | undefined;
  return nested?.id;
};

const toMoney = (value: number | string | null | undefined) =>
  new Intl.NumberFormat("es-CR", {
    style: "currency",
    currency: "CRC",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  error?.message ||
  fallback;

const getProductCombinationPrices = (product: Product) =>
  (product.productVariationCombinations || [])
    .map((combination) => Number(combination.price))
    .filter((price) => Number.isFinite(price));

const getProductPriceRange = (product: Product) => {
  const prices = getProductCombinationPrices(product);

  if (prices.length > 0) {
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    return minPrice === maxPrice ? toMoney(minPrice) : `${toMoney(minPrice)} - ${toMoney(maxPrice)}`;
  }

  return product.price !== undefined && product.price !== null ? toMoney(product.price) : "-";
};

const getProductAvailable = (product: Product) =>
  (product.productVariationCombinations || [])
    .map((combination) => Number(combination.available ?? 0))
    .filter((available) => Number.isFinite(available))
    .reduce((total, available) => total + available, 0);

const getCombinationValues = (combination: ProductVariationCombination) =>
  combination.values ||
  combination.combinationValues ||
  combination.productVariationCombinationValues ||
  [];

const getCombinationValueId = (relation: ProductVariationCombinationValue) =>
  relation.productVariationValueId ?? relation.productVariationValue?.id;

const getCombinationRelationId = (relation: ProductVariationCombinationValue, fallbackCombinationId: number) =>
  relation.combinationId ?? relation.productVariationCombinationId ?? relation.productVariationCombination?.id ?? fallbackCombinationId;

const getPhotoSetKey = (photoSet: ProductVariationPhotoSet) =>
  photoSet.combinationKey ||
  photoSet.combination_key ||
  buildCombinationKey(photoSet.valueIds || []);

const buildCombinationKey = (valueIds: number[]) =>
  [...valueIds]
    .filter((valueId) => Number.isInteger(valueId) && valueId > 0)
    .sort((a, b) => a - b)
    .join("-");

const toSelectOption = (item?: IdName): SelectOption | null => {
  if (!item?.id) return null;
  return {
    value: item.id,
    label: getItemLabel(item),
  };
};

const mergeOptions = (base: SelectOption[], extra: SelectOption[]) => {
  const byId = new Map<number, SelectOption>();
  [...base, ...extra].forEach((option) => byId.set(option.value, option));
  return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const generateSku = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 900000) + 100000;
  return `SKU-${timestamp}${random}`;
};

const Products: React.FC = () => {
  const navigate = useNavigate();
  const { user: loggedUser } = useSelector((state: RootState) => state.auth);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [productModalAlert, setProductModalAlert] = useState<AlertState | null>(null);
  const [valuesModalAlert, setValuesModalAlert] = useState<AlertState | null>(null);
  const [deleteModalAlert, setDeleteModalAlert] = useState<AlertState | null>(null);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [expandedProductIds, setExpandedProductIds] = useState<number[]>([]);
  const pageSize = 10;

  const [showModal, setShowModal] = useState(false);
  const [mode, setMode] = useState<ModalMode>("create");
  const [editProduct, setEditProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([]);
  const [variationOptions, setVariationOptions] = useState<SelectOption[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);

  const [deleteProduct, setDeleteProduct] = useState<Product | null>(null);

  const [showValuesModal, setShowValuesModal] = useState(false);
  const [valuesProduct, setValuesProduct] = useState<Product | null>(null);
  const [valueForm, setValueForm] = useState<VariationValueForm | null>(null);
  const [combinationForm, setCombinationForm] = useState<CombinationForm | null>(null);
  const [photoSetForm, setPhotoSetForm] = useState<PhotoSetForm | null>(null);

  const storeId = useMemo(() => {
    const user = loggedUser as any;
    return user?.storeId || user?.store_id || user?.store?.id || user?.stores?.[0]?.id;
  }, [loggedUser]);

  const fetchProductCategories = useCallback(async (productId: number) => {
    const res = await axios.get<PageResponse<ProductCategory>>(API_URL_PRODUCT_CATEGORIES, {
      headers: authHeader(),
      params: { productId, page: 0, size: 100 },
    });
    return getPageItems<ProductCategory>(res.data);
  }, []);

  const fetchCatalogOptions = useCallback(async () => {
    setLoadingCatalogs(true);
    try {
      const [categoriesRes, variationsRes] = await Promise.all([
        axios
          .get<PageResponse<IdName>>(API_URL_CATEGORIES, {
            headers: authHeader(),
            params: { page: 0, size: 500 },
          })
          .catch(() => ({ data: [] as IdName[] })),
        axios
          .get<PageResponse<IdName>>(API_URL_VARIATIONS, {
            headers: authHeader(),
            params: { page: 0, size: 500 },
          })
          .catch(() => ({ data: [] as IdName[] })),
      ]);

      const fetchedCategories = getPageItems<IdName>(categoriesRes.data)
          .map(toSelectOption)
          .filter((option): option is SelectOption => !!option);
      const fetchedVariations = getPageItems<IdName>(variationsRes.data)
          .map(toSelectOption)
          .filter((option): option is SelectOption => !!option);

      setCategoryOptions((current) => mergeOptions(current, fetchedCategories));
      setVariationOptions((current) => mergeOptions(current, fetchedVariations));
    } finally {
      setLoadingCatalogs(false);
    }
  }, []);

  const fetchVariationValues = useCallback(async (productVariationId: number) => {
    const res = await axios.get<PageResponse<ProductVariationValue>>(API_URL_PRODUCT_VARIATION_VALUES, {
      headers: authHeader(),
      params: { productVariationId, page: 0, size: 100 },
    });
    return getPageItems<ProductVariationValue>(res.data);
  }, []);

  const fetchProductVariations = useCallback(async (productId: number) => {
    const res = await axios.get<PageResponse<ProductVariation>>(API_URL_PRODUCT_VARIATIONS, {
      headers: authHeader(),
      params: { productId, page: 0, size: 100 },
    });
    const variations = getPageItems<ProductVariation>(res.data);
    return Promise.all(
      variations.map(async (variation) => ({
        ...variation,
        values: await fetchVariationValues(variation.id),
      }))
    );
  }, [fetchVariationValues]);

  const fetchCombinationValues = useCallback(async (productVariationCombinationId: number) => {
    const res = await axios.get<PageResponse<ProductVariationCombinationValue>>(
      API_URL_PRODUCT_VARIATION_COMBINATION_VALUES,
      {
        headers: authHeader(),
        params: { combinationId: productVariationCombinationId, page: 0, size: 100 },
      }
    );
    return getPageItems<ProductVariationCombinationValue>(res.data);
  }, []);

  const fetchProductVariationCombinations = useCallback(async (productId: number) => {
    const res = await axios.get<PageResponse<ProductVariationCombination>>(
      API_URL_PRODUCT_VARIATION_COMBINATIONS,
      {
        headers: authHeader(),
        params: { productId, page: 0, size: 200 },
      }
    );
    const combinations = getPageItems<ProductVariationCombination>(res.data);
    return Promise.all(
      combinations.map(async (combination) => ({
        ...combination,
        values: await fetchCombinationValues(combination.id).catch(() => getCombinationValues(combination)),
      }))
    );
  }, [fetchCombinationValues]);

  const fetchPhotoSetValues = useCallback(async (photoSetId: number) => {
    const res = await axios.get<PageResponse<ProductVariationPhotoSetValue>>(
      API_URL_PRODUCT_VARIATION_PHOTO_SET_VALUES,
      {
        headers: authHeader(),
        params: { photoSetId, page: 0, size: 100 },
      }
    );
    return getPageItems<ProductVariationPhotoSetValue>(res.data);
  }, []);

  const fetchPhotoSetPhotos = useCallback(async (photoSetId: number) => {
    const res = await axios.get<PageResponse<ProductVariationPhotoSetPhoto>>(
      API_URL_PRODUCT_VARIATION_PHOTO_SET_PHOTOS,
      {
        headers: authHeader(),
        params: { photoSetId, page: 0, size: 100 },
      }
    );
    return getPageItems<ProductVariationPhotoSetPhoto>(res.data).sort(
      (a, b) => Number(a.position ?? 0) - Number(b.position ?? 0) || a.id - b.id
    );
  }, []);

  const fetchProductVariationPhotoSets = useCallback(async (productId: number) => {
    const res = await axios.get<PageResponse<ProductVariationPhotoSet>>(
      API_URL_PRODUCT_VARIATION_PHOTO_SETS,
      {
        headers: authHeader(),
        params: { productId, page: 0, size: 200 },
      }
    );
    const photoSets = getPageItems<ProductVariationPhotoSet>(res.data);
    return Promise.all(
      photoSets.map(async (photoSet) => {
        const [values, photos] = await Promise.all([
          fetchPhotoSetValues(photoSet.id).catch(() => []),
          fetchPhotoSetPhotos(photoSet.id).catch(() => []),
        ]);
        const valueIds = values
          .map((value) => value.productVariationValueId ?? value.productVariationValue?.id)
          .filter((valueId): valueId is number => !!valueId);

        return {
          ...photoSet,
          valueIds,
          values,
          photos,
        };
      })
    );
  }, [fetchPhotoSetPhotos, fetchPhotoSetValues]);

  const enrichProducts = useCallback(async (items: Product[]) =>
    Promise.all(
      items.map(async (product) => {
        const [productCategories, productVariations, productVariationCombinations, productVariationPhotoSets] = await Promise.all([
          fetchProductCategories(product.id).catch(() => []),
          fetchProductVariations(product.id).catch(() => []),
          fetchProductVariationCombinations(product.id).catch(() => []),
          fetchProductVariationPhotoSets(product.id).catch(() => []),
        ]);

        return {
          ...product,
          productCategories,
          productVariations,
          productVariationCombinations,
          productVariationPhotoSets,
        };
      })
    ), [fetchProductCategories, fetchProductVariations, fetchProductVariationCombinations, fetchProductVariationPhotoSets]);

  const fetchProducts = useCallback(async () => {
    if (!loggedUser) return;

    setLoading(true);
    setAlert(null);
    try {
      const response = await axios.get<PageResponse<Product>>(API_URL_PRODUCTS, {
        params: {
          keyword: searchTerm,
          storeId,
          page: currentPage - 1,
          size: pageSize,
        },
        headers: authHeader(),
      });

      const items = getPageItems<Product>(response.data);
      setProducts(await enrichProducts(items));
      setTotalPages(Math.max(1, response.data?.totalPages || 1));
    } catch (error: any) {
      setProducts([]);
      setTotalPages(1);
      setAlert({ variant: "danger", message: getErrorMessage(error, "No se pudieron cargar los productos.") });
    } finally {
      setLoading(false);
    }
  }, [currentPage, enrichProducts, loggedUser, searchTerm, storeId]);

  useEffect(() => {
    if (!loggedUser) {
      navigate("/login");
      return;
    }

    fetchProducts();
    fetchCatalogOptions();
  }, [fetchCatalogOptions, fetchProducts, loggedUser, navigate]);

  const openCreate = () => {
    setMode("create");
    setEditProduct(null);
    setForm({
      ...emptyForm,
      sku: generateSku(),
    });
    setAlert(null);
    setProductModalAlert(null);
    fetchCatalogOptions();
    setShowModal(true);
  };

  const toggleProductDetails = (productId: number) => {
    setExpandedProductIds((current) =>
      current.includes(productId) ? current.filter((id) => id !== productId) : [...current, productId]
    );
  };

  const openEdit = (product: Product) => {
    const categoryIds = (product.productCategories || [])
      .map((relation) => getRelationId(relation, "category"))
      .filter((id): id is number => !!id);
    const variationIds = (product.productVariations || [])
      .map((relation) => getRelationId(relation, "variation"))
      .filter((id): id is number => !!id);
    const relatedCategoryOptions = (product.productCategories || [])
      .map((relation) => relation.category)
      .map(toSelectOption)
      .filter((option): option is SelectOption => !!option);
    const relatedVariationOptions = (product.productVariations || [])
      .map((relation) => relation.variation)
      .map(toSelectOption)
      .filter((option): option is SelectOption => !!option);

    setMode("edit");
    setEditProduct(product);
    setCategoryOptions((current) => mergeOptions(current, relatedCategoryOptions));
    setVariationOptions((current) => mergeOptions(current, relatedVariationOptions));
    setForm({
      sku: product.sku || "",
      name: product.name || "",
      description: product.description || "",
      categoryIds,
      variationIds,
    });
    setAlert(null);
    setProductModalAlert(null);
    fetchCatalogOptions();
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setProductModalAlert(null);
    setShowModal(false);
  };

  const validateForm = () => {
    if (!form.name.trim()) return "El nombre es requerido.";
    return null;
  };

  const syncCategoryRelations = async (product: Product, categoryIds: number[]) => {
    const currentRelations = await fetchProductCategories(product.id).catch(() => []);
    const currentIds = new Set(currentRelations.map((relation) => getRelationId(relation, "category")).filter(Boolean));
    const nextIds = new Set(categoryIds);

    await Promise.all([
      ...currentRelations
        .filter((relation) => {
          const categoryId = getRelationId(relation, "category");
          return categoryId && !nextIds.has(categoryId);
        })
        .map((relation) =>
          axios.delete(`${API_URL_PRODUCT_CATEGORIES}/${relation.id}`, { headers: authHeader() })
        ),
      ...categoryIds
        .filter((categoryId) => !currentIds.has(categoryId))
        .map((categoryId) =>
          axios.post(
            API_URL_PRODUCT_CATEGORIES,
            {
              productId: product.id,
              categoryId,
              product: { id: product.id },
              category: { id: categoryId },
            },
            { headers: authHeader() }
          )
        ),
    ]);
  };

  const syncVariationRelations = async (product: Product, variationIds: number[]) => {
    const currentRelations = await fetchProductVariations(product.id).catch(() => []);
    const currentIds = new Set(currentRelations.map((relation) => getRelationId(relation, "variation")).filter(Boolean));
    const nextIds = new Set(variationIds);

    await Promise.all([
      ...currentRelations
        .filter((relation) => {
          const variationId = getRelationId(relation, "variation");
          return variationId && !nextIds.has(variationId);
        })
        .map((relation) =>
          axios.delete(`${API_URL_PRODUCT_VARIATIONS}/${relation.id}`, { headers: authHeader() })
        ),
      ...variationIds
        .filter((variationId) => !currentIds.has(variationId))
        .map((variationId) =>
          axios.post(
            API_URL_PRODUCT_VARIATIONS,
            {
              productId: product.id,
              variationId,
              product: { id: product.id },
              variation: { id: variationId },
            },
            { headers: authHeader() }
          )
        ),
    ]);
  };

  const saveProduct = async () => {
    const error = validateForm();
    if (error) {
      setProductModalAlert({ variant: "warning", message: error });
      return;
    }

    setSaving(true);
    setProductModalAlert(null);
    try {
      const productName = form.name.trim();
      const productDescription = form.description.trim();
      const productSku = form.sku || editProduct?.sku || generateSku();
      const payload: any = {
        sku: productSku,
        name: productName,
        description: productDescription,
        price: Number(editProduct?.price ?? 0),
      };

      if (storeId) {
        payload.storeId = storeId;
      }

      let savedProduct: Product;

      if (mode === "create") {
        const response = await axios.post<Product>(API_URL_PRODUCTS, payload, { headers: authHeader() });
        savedProduct = response.data;
      } else {
        if (!editProduct?.id) throw new Error("No hay producto seleccionado para editar.");

        const productChanged =
          productSku !== (editProduct.sku || "") ||
          productName !== (editProduct.name || "") ||
          productDescription !== (editProduct.description || "");

        if (productChanged) {
          const response = await axios.put<Product>(
            `${API_URL_PRODUCTS}/${editProduct.id}`,
            { ...payload, id: editProduct.id },
            { headers: authHeader() }
          );
          savedProduct = response.data;
        } else {
          savedProduct = editProduct;
        }
      }

      await Promise.all([
        syncCategoryRelations(savedProduct, form.categoryIds),
        syncVariationRelations(savedProduct, form.variationIds),
      ]);

      setShowModal(false);
      setCurrentPage(1);
      setAlert({
        variant: "success",
        message: mode === "create" ? "Producto creado correctamente." : "Producto actualizado correctamente.",
      });
      await fetchProducts();
    } catch (error: any) {
      setProductModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo guardar el producto.") });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteProduct) return;
    setSaving(true);
    setDeleteModalAlert(null);
    try {
      await axios.delete(`${API_URL_PRODUCTS}/${deleteProduct.id}`, { headers: authHeader() });
      setDeleteProduct(null);
      setAlert({ variant: "success", message: "Producto eliminado correctamente." });
      await fetchProducts();
    } catch (error: any) {
      setDeleteModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo eliminar el producto.") });
    } finally {
      setSaving(false);
    }
  };

  const openValues = (product: Product) => {
    setValuesProduct(product);
    setValueForm(null);
    setCombinationForm(null);
    setPhotoSetForm(null);
    setValuesModalAlert(null);
    setShowValuesModal(true);
  };

  const getProductVariationLabel = (productVariationId: number) => {
    const relation = valuesProduct?.productVariations?.find((variation) => variation.id === productVariationId);
    const label = getItemLabel(relation?.variation);
    return label === "-" ? "" : label;
  };

  const refreshValuesProduct = async (productId?: number) => {
    if (!productId) return;

    const response = await axios.get<Product>(`${API_URL_PRODUCTS}/${productId}`, {
      headers: authHeader(),
    });
    const [productCategories, productVariations, productVariationPhotoSets] = await Promise.all([
      fetchProductCategories(productId).catch(() => []),
      fetchProductVariations(productId).catch(() => []),
      fetchProductVariationPhotoSets(productId).catch(() => []),
    ]);
    const updatedProduct = {
      ...response.data,
      productCategories,
      productVariations,
      productVariationCombinations: await fetchProductVariationCombinations(productId).catch(() => []),
      productVariationPhotoSets,
    };

    setValuesProduct(updatedProduct);
    setProducts((current) =>
      current.map((product) => (product.id === productId ? updatedProduct : product))
    );
  };

  const startValueCreate = (productVariationId: number) => {
    setValueForm({
      productVariationId,
      name: getProductVariationLabel(productVariationId),
      value: "",
    });
  };

  const startValueEdit = (productVariationId: number, value: ProductVariationValue) => {
    setValueForm({
      id: value.id,
      productVariationId,
      name: value.name || getProductVariationLabel(productVariationId),
      value: value.value || "",
    });
  };

  const saveVariationValue = async () => {
    if (!valueForm) return;
    if (!valueForm.value.trim()) {
      setValuesModalAlert({ variant: "warning", message: "Ingrese el valor de la variacion." });
      return;
    }
    setSaving(true);
    setValuesModalAlert(null);
    try {
      const payload: any = {
        productVariationId: valueForm.productVariationId,
        name: valueForm.name.trim(),
        value: valueForm.value.trim(),
      };

      if (valueForm.id) {
        await axios.put(
          `${API_URL_PRODUCT_VARIATION_VALUES}/${valueForm.id}`,
          { ...payload, id: valueForm.id },
          { headers: authHeader() }
        );
      } else {
        await axios.post(API_URL_PRODUCT_VARIATION_VALUES, payload, { headers: authHeader() });
      }

      setValueForm(null);
      await refreshValuesProduct(valuesProduct?.id);
      await fetchProducts();
      setValuesModalAlert({ variant: "success", message: "Valor de variacion guardado correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo guardar el valor de variacion.") });
    } finally {
      setSaving(false);
    }
  };

  const deleteVariationValue = async (id: number) => {
    setSaving(true);
    setValuesModalAlert(null);
    try {
      await axios.delete(`${API_URL_PRODUCT_VARIATION_VALUES}/${id}`, { headers: authHeader() });
      await refreshValuesProduct(valuesProduct?.id);
      await fetchProducts();
      setValuesModalAlert({ variant: "success", message: "Valor de variacion eliminado correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo eliminar el valor de variacion.") });
    } finally {
      setSaving(false);
    }
  };

  const getCombinationLabel = (combination: ProductVariationCombination) => {
    const values = getCombinationValues(combination);
    if (!values.length) return `Combinacion #${combination.id}`;

    return values
      .map((relation) => relation.productVariationValue?.value || relation.productVariationValue?.name || `Valor ${getCombinationValueId(relation) ?? "-"}`)
      .join(" / ");
  };

  const getCombinationCode = (combination: ProductVariationCombination) =>
    combination.combinationKey ||
    combination.combination_key ||
    combination.varCode ||
    buildCombinationKey(getCombinationValues(combination).map(getCombinationValueId).filter((id): id is number => !!id));

  const startCombinationCreate = () => {
    setCombinationForm({
      selectedValueIds: {},
      varCode: "",
      price: "",
      available: "0",
    });
  };

  const startCombinationEdit = (combination: ProductVariationCombination) => {
    const selectedValueIds: Record<number, number> = {};

    getCombinationValues(combination).forEach((relation) => {
      const value = relation.productVariationValue;
      const valueId = getCombinationValueId(relation);
      const productVariationId = value?.productVariationId ?? value?.productVariation?.id;
      if (productVariationId && valueId) {
        selectedValueIds[productVariationId] = valueId;
      }
    });

    setCombinationForm({
      id: combination.id,
      selectedValueIds,
      varCode: combination.varCode || "",
      price: combination.price === undefined || combination.price === null ? "" : String(combination.price),
      available: combination.available === undefined || combination.available === null ? "0" : String(combination.available),
    });
  };

  const syncCombinationValues = async (combination: ProductVariationCombination, selectedValueIds: number[]) => {
    const currentValues = await fetchCombinationValues(combination.id).catch(() => getCombinationValues(combination));
    const currentIds = new Set(currentValues.map(getCombinationValueId).filter(Boolean));
    const nextIds = new Set(selectedValueIds);

    await Promise.all([
      ...currentValues
        .filter((relation) => {
          const valueId = getCombinationValueId(relation);
          return valueId && !nextIds.has(valueId);
        })
        .map((relation) => {
          const valueId = getCombinationValueId(relation);
          const combinationId = getCombinationRelationId(relation, combination.id);
          return axios.delete(`${API_URL_PRODUCT_VARIATION_COMBINATION_VALUES}/${combinationId}/${valueId}`, {
            headers: authHeader(),
          });
        }),
      ...selectedValueIds
        .filter((valueId) => !currentIds.has(valueId))
        .map((valueId) =>
          axios.post(
            API_URL_PRODUCT_VARIATION_COMBINATION_VALUES,
            {
              combinationId: combination.id,
              productVariationValueId: valueId,
            },
            { headers: authHeader() }
          )
        ),
    ]);
  };

  const saveCombination = async () => {
    if (!valuesProduct || !combinationForm) return;

    const selectedValueIds = Object.values(combinationForm.selectedValueIds)
      .map(Number)
      .filter((valueId) => Number.isInteger(valueId) && valueId > 0);
    const combinationKey = buildCombinationKey(selectedValueIds);
    const varCode = combinationForm.varCode.trim();

    if (selectedValueIds.length === 0) {
      setValuesModalAlert({ variant: "warning", message: "Seleccione al menos un valor para formar la combinacion." });
      return;
    }
    if (!varCode) {
      setValuesModalAlert({ variant: "warning", message: "El var-code de la combinacion es requerido." });
      return;
    }
    if (combinationForm.price === "" || Number.isNaN(Number(combinationForm.price)) || Number(combinationForm.price) < 0) {
      setValuesModalAlert({ variant: "warning", message: "El precio de la combinacion es requerido y no puede ser negativo." });
      return;
    }
    if (combinationForm.available === "" || Number.isNaN(Number(combinationForm.available)) || Number(combinationForm.available) < 0) {
      setValuesModalAlert({ variant: "warning", message: "La disponibilidad de la combinacion es requerida y no puede ser negativa." });
      return;
    }

    setSaving(true);
    setValuesModalAlert(null);
    try {
      const payload = {
        productId: valuesProduct.id,
        varCode,
        combinationKey,
        price: Number(combinationForm.price),
        available: Number(combinationForm.available),
      };

      const response = combinationForm.id
        ? await axios.put<ProductVariationCombination>(
            `${API_URL_PRODUCT_VARIATION_COMBINATIONS}/${combinationForm.id}`,
            { ...payload, id: combinationForm.id },
            { headers: authHeader() }
          )
        : await axios.post<ProductVariationCombination>(API_URL_PRODUCT_VARIATION_COMBINATIONS, payload, {
            headers: authHeader(),
          });

      await syncCombinationValues(response.data, selectedValueIds);
      setCombinationForm(null);
      await refreshValuesProduct(valuesProduct.id);
      await fetchProducts();
      setValuesModalAlert({ variant: "success", message: "Combinacion guardada correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo guardar la combinacion.") });
    } finally {
      setSaving(false);
    }
  };

  const deleteCombination = async (combination: ProductVariationCombination) => {
    setSaving(true);
    setValuesModalAlert(null);
    try {
      await Promise.all(
        getCombinationValues(combination)
          .filter((relation) => !!getCombinationValueId(relation))
          .map((relation) => {
            const valueId = getCombinationValueId(relation);
            const combinationId = getCombinationRelationId(relation, combination.id);
            return axios.delete(`${API_URL_PRODUCT_VARIATION_COMBINATION_VALUES}/${combinationId}/${valueId}`, {
              headers: authHeader(),
            });
          })
      );
      await axios.delete(`${API_URL_PRODUCT_VARIATION_COMBINATIONS}/${combination.id}`, { headers: authHeader() });
      await refreshValuesProduct(valuesProduct?.id);
      await fetchProducts();
      setValuesModalAlert({ variant: "success", message: "Combinacion eliminada correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo eliminar la combinacion.") });
    } finally {
      setSaving(false);
    }
  };

  const getCombinationSelectedValueIds = (combination: ProductVariationCombination) =>
    getCombinationValues(combination)
      .map(getCombinationValueId)
      .filter((valueId): valueId is number => !!valueId);

  const findPhotoSetForValueIds = (valueIds: number[]) => {
    const key = buildCombinationKey(valueIds);
    return valuesProduct?.productVariationPhotoSets?.find((photoSet) => getPhotoSetKey(photoSet) === key);
  };

  const getVariationValueById = (valueId: number) => {
    for (const variation of valuesProduct?.productVariations || []) {
      const value = (variation.values || []).find((item) => item.id === valueId);
      if (value) {
        return {
          value,
          variationLabel: getItemLabel(variation.variation),
        };
      }
    }

    return null;
  };

  const getPhotoSetScopeLabel = (valueIds: number[]) =>
    valueIds
      .map((valueId) => {
        const match = getVariationValueById(valueId);
        return match
          ? `${match.variationLabel}: ${match.value.value || match.value.name || `Valor #${valueId}`}`
          : `Valor #${valueId}`;
      })
      .join(" / ");

  const startPhotoSetForCombination = (combination: ProductVariationCombination) => {
    const selectedValueIds = getCombinationSelectedValueIds(combination);
    const existingPhotoSet = findPhotoSetForValueIds(selectedValueIds);

    setValueForm(null);
    setCombinationForm(null);
    setPhotoSetForm({
      photoSetId: existingPhotoSet?.id,
      combinationId: combination.id,
      availableValueIds: selectedValueIds,
      selectedValueIds,
      name: existingPhotoSet?.name || getCombinationLabel(combination),
      priority: String(existingPhotoSet?.priority ?? 0),
      active: existingPhotoSet?.active ?? true,
      files: [],
      photos: existingPhotoSet?.photos || [],
    });
  };

  const updatePhotoSetScope = (valueId: number, checked: boolean) => {
    setPhotoSetForm((prev) => {
      if (!prev) return prev;

      const selectedValueIds = checked
        ? [...prev.selectedValueIds, valueId]
        : prev.selectedValueIds.filter((id) => id !== valueId);
      const normalizedValueIds = Array.from(new Set(selectedValueIds)).sort((a, b) => a - b);
      const existingPhotoSet = findPhotoSetForValueIds(normalizedValueIds);

      return {
        ...prev,
        photoSetId: existingPhotoSet?.id,
        selectedValueIds: normalizedValueIds,
        name: existingPhotoSet?.name || getPhotoSetScopeLabel(normalizedValueIds) || prev.name,
        priority: String(existingPhotoSet?.priority ?? prev.priority),
        active: existingPhotoSet?.active ?? prev.active,
        files: [],
        photos: existingPhotoSet?.photos || [],
      };
    });
  };

  const updatePhotoSetPhotoInForm = (photoId: number, patch: Partial<ProductVariationPhotoSetPhoto>) => {
    setPhotoSetForm((prev) =>
      prev
        ? {
            ...prev,
            photos: prev.photos.map((photo) => (photo.id === photoId ? { ...photo, ...patch } : photo)),
          }
        : prev
    );
  };

  const savePhotoSet = async () => {
    if (!valuesProduct || !photoSetForm) return;

    const selectedValueIds = photoSetForm.selectedValueIds
      .map(Number)
      .filter((valueId) => Number.isInteger(valueId) && valueId > 0);
    const combinationKey = buildCombinationKey(selectedValueIds);

    if (!selectedValueIds.length) {
      setValuesModalAlert({ variant: "warning", message: "Seleccione una combinacion valida para asociar fotos." });
      return;
    }

    if (photoSetForm.priority === "" || Number.isNaN(Number(photoSetForm.priority))) {
      setValuesModalAlert({ variant: "warning", message: "La prioridad debe ser un numero valido." });
      return;
    }

    setSaving(true);
    setValuesModalAlert(null);
    try {
      let photoSetId = photoSetForm.photoSetId;
      const payload = {
        productId: valuesProduct.id,
        combinationKey,
        name: photoSetForm.name.trim() || null,
        priority: Number(photoSetForm.priority),
        active: photoSetForm.active,
      };

      if (photoSetId) {
        await axios.put(
          `${API_URL_PRODUCT_VARIATION_PHOTO_SETS}/${photoSetId}`,
          { ...payload, id: photoSetId },
          { headers: authHeader() }
        );
      } else {
        const response = await axios.post<{ photoSet: ProductVariationPhotoSet }>(
          `${API_URL_PRODUCT_VARIATION_PHOTO_SETS}/with-values`,
          {
            productId: valuesProduct.id,
            name: payload.name,
            priority: payload.priority,
            active: payload.active,
            valueIds: selectedValueIds,
          },
          { headers: authHeader() }
        );
        photoSetId = response.data.photoSet.id;
      }

      if (photoSetId && photoSetForm.files.length) {
        await Promise.all(
          photoSetForm.files.map((file, index) => {
            const formData = new FormData();
            formData.append("photoSetId", String(photoSetId));
            formData.append("file", file);
            formData.append("position", String(photoSetForm.photos.length + index));
            formData.append("primary", String(photoSetForm.photos.length === 0 && index === 0));

            return axios.post(API_URL_PRODUCT_VARIATION_PHOTO_SET_PHOTOS + "/upload", formData, {
              headers: authHeader(),
            });
          })
        );
      }

      await refreshValuesProduct(valuesProduct.id);
      await fetchProducts();
      const refreshedPhotos = photoSetId
        ? await fetchPhotoSetPhotos(photoSetId).catch(() => photoSetForm.photos)
        : photoSetForm.photos;
      setPhotoSetForm((prev) =>
        prev
          ? {
              ...prev,
              photoSetId,
              files: [],
              photos: refreshedPhotos,
            }
          : prev
      );
      setValuesModalAlert({ variant: "success", message: "Fotos de producto guardadas correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudieron guardar las fotos del producto.") });
    } finally {
      setSaving(false);
    }
  };

  const savePhotoMetadata = async (photo: ProductVariationPhotoSetPhoto) => {
    setSaving(true);
    setValuesModalAlert(null);
    try {
      const photosToPersist = photo.primary
        ? (photoSetForm?.photos || []).map((item) => ({ ...item, primary: item.id === photo.id }))
        : [photo];

      await Promise.all(
        photosToPersist.map((item) =>
          axios.put(
            `${API_URL_PRODUCT_VARIATION_PHOTO_SET_PHOTOS}/${item.id}`,
            item,
            { headers: authHeader() }
          )
        )
      );
      await refreshValuesProduct(valuesProduct?.id);
      setValuesModalAlert({ variant: "success", message: "Foto actualizada correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo actualizar la foto.") });
    } finally {
      setSaving(false);
    }
  };

  const deletePhoto = async (photoId: number) => {
    setSaving(true);
    setValuesModalAlert(null);
    try {
      await axios.delete(`${API_URL_PRODUCT_VARIATION_PHOTO_SET_PHOTOS}/${photoId}`, { headers: authHeader() });
      setPhotoSetForm((prev) =>
        prev ? { ...prev, photos: prev.photos.filter((photo) => photo.id !== photoId) } : prev
      );
      await refreshValuesProduct(valuesProduct?.id);
      setValuesModalAlert({ variant: "success", message: "Foto eliminada correctamente." });
    } catch (error: any) {
      setValuesModalAlert({ variant: "danger", message: getErrorMessage(error, "No se pudo eliminar la foto.") });
    } finally {
      setSaving(false);
    }
  };

  const searched = () => {
    setSearchTerm(tempSearchTerm);
    setCurrentPage(1);
  };

  const getSelectedOptions = (ids: number[], options: SelectOption[]) =>
    ids.map((id) => options.find((option) => option.value === id) || { value: id, label: `ID ${id}` });

  const handleCategoryChange = (selected: MultiValue<SelectOption>) => {
    setForm((prev) => ({
      ...prev,
      categoryIds: selected.map((option) => option.value),
    }));
  };

  const handleVariationChange = (selected: MultiValue<SelectOption>) => {
    setForm((prev) => ({
      ...prev,
      variationIds: selected.map((option) => option.value),
    }));
  };

  const renderCategories = (product: Product) => {
    const relations = product.productCategories || [];
    if (!relations.length) return <span className="text-muted">Sin categorias</span>;

    return (
      <div className="d-flex flex-wrap gap-1">
        {relations.map((relation) => (
          <Badge bg="secondary" key={relation.id}>
            {getItemLabel(relation.category)} ({getRelationId(relation, "category") || "-"})
          </Badge>
        ))}
      </div>
    );
  };

  const renderVariations = (product: Product) => {
    const relations = product.productVariations || [];
    if (!relations.length) return <span className="text-muted">Sin variaciones</span>;

    return (
      <div className="d-flex flex-column gap-1">
        {relations.map((relation) => {
          const values = relation.values || relation.productVariationValues || [];
          return (
            <div key={relation.id}>
              <Badge bg="info" text="dark">
                {getItemLabel(relation.variation)} ({getRelationId(relation, "variation") || "-"})
              </Badge>
              {values.length > 0 && (
                <span className="text-muted ms-2" style={{ fontSize: 12 }}>
                  {values.map((value) => value.value || value.name || `ID ${value.id}`).join(", ")}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Container className="py-4">
      <Card className="shadow-sm border-0">
        <Card.Body>
          <Row className="align-items-center g-3">
            <Col md={6}>
              <h4 className="mb-1">Productos</h4>
              <div className="text-muted" style={{ fontSize: 13 }}>
                Administre productos, categorias, variaciones y valores.
              </div>
            </Col>

            <Col md={6} className="d-flex justify-content-md-end gap-2">
              <OverlayTrigger placement="top" overlay={<Tooltip>Recargar</Tooltip>}>
                <Button variant="outline-secondary" onClick={fetchProducts} disabled={loading}>
                  <FaSyncAlt className="me-2" />
                  Recargar
                </Button>
              </OverlayTrigger>

              <Button variant="primary" onClick={openCreate}>
                <FaPlus className="me-2" />
                Nuevo producto
              </Button>
            </Col>
          </Row>

          {alert && (
            <Alert className="mt-3" variant={alert.variant} onClose={() => setAlert(null)} dismissible>
              {alert.message}
            </Alert>
          )}

          <Row className="mt-3 g-3">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <FaSearch />
                </InputGroup.Text>
                <Form.Control
                  className="mt-0"
                  placeholder="Buscar por nombre de producto..."
                  value={tempSearchTerm}
                  onChange={(event) => setTempSearchTerm(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") searched();
                  }}
                />
                <Button variant="primary" onClick={searched}>
                  Buscar
                </Button>
              </InputGroup>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end align-items-center">
              <div className="text-muted" style={{ fontSize: 13 }}>
                Pagina <b>{currentPage}</b> de <b>{totalPages}</b>
              </div>
            </Col>
          </Row>

          <div className="table-responsive mt-3">
            <Table hover bordered className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 52 }}></th>
                  <th style={{ width: 70 }}>ID</th>
                  <th>Producto</th>
                  <th style={{ width: 120 }}>Disponible</th>
                  <th>Categorias</th>
                  <th>Variaciones</th>
                  <th style={{ width: 260 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5">
                      <Spinner animation="border" className="me-2" />
                      Cargando productos...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      No hay productos para mostrar.
                    </td>
                  </tr>
                ) : (
                  products.map((product) => {
                    const isExpanded = expandedProductIds.includes(product.id);

                    return (
                      <React.Fragment key={product.id}>
                        <tr>
                          <td className="text-center">
                            <Button
                              variant="light"
                              size="sm"
                              className="product-details-toggle"
                              aria-label={isExpanded ? "Ocultar detalles" : "Mostrar detalles"}
                              aria-expanded={isExpanded}
                              onClick={() => toggleProductDetails(product.id)}
                            >
                              {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </Button>
                          </td>
                          <td>{product.id}</td>
                          <td>
                            <div className="fw-semibold">{product.name}</div>
                            <div className="text-muted" style={{ fontSize: 13 }}>
                              {product.description || "-"}
                            </div>
                          </td>
                          <td>{getProductAvailable(product)}</td>
                          <td>{renderCategories(product)}</td>
                          <td>{renderVariations(product)}</td>
                          <td>
                            <div className="d-flex flex-wrap gap-2">
                              <Button variant="outline-secondary" size="sm" onClick={() => openValues(product)}>
                                <FaLayerGroup className="me-1" />
                                Inventario
                              </Button>
                              <Button variant="outline-primary" size="sm" onClick={() => openEdit(product)}>
                                <FaEdit className="me-1" />
                                Editar
                              </Button>
                              <Button
                                variant="outline-danger"
                                className="btn-delete"
                                size="sm"
                                onClick={() => {
                                  setDeleteModalAlert(null);
                                  setDeleteProduct(product);
                                }}
                              >
                                <FaTrash className="me-1" />
                                Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="product-details-row">
                            <td colSpan={7}>
                              <div className="product-details-panel">
                                <Row className="g-3">
                                  <Col md={4}>
                                    <div className="product-detail-label">SKU</div>
                                    <div className="product-detail-value">{product.sku || "-"}</div>
                                  </Col>
                                  <Col md={4}>
                                    <div className="product-detail-label">Precio</div>
                                    <div className="product-detail-value">{getProductPriceRange(product)}</div>
                                  </Col>
                                  <Col md={12}>
                                    <div className="product-detail-label">Descripcion</div>
                                    <div className="product-detail-value">{product.description || "-"}</div>
                                  </Col>
                                </Row>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Siguiente
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={closeModal} centered size="lg" backdrop={saving ? "static" : true}>
        <Modal.Header closeButton={!saving}>
          <Modal.Title>{mode === "create" ? "Nuevo producto" : "Editar producto"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {productModalAlert && (
            <Alert variant={productModalAlert.variant} onClose={() => setProductModalAlert(null)} dismissible>
              {productModalAlert.message}
            </Alert>
          )}
          <Row className="g-3">
            <Col md={5}>
              <Form.Label>SKU</Form.Label>
              <Form.Control value={form.sku} disabled />
            </Col>
            <Col md={12}>
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                disabled={saving}
              />
            </Col>
            <Col md={12}>
              <Form.Label>Descripcion</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                disabled={saving}
              />
            </Col>
            <Col md={6}>
              <Form.Label>
                <FaTag className="me-1" />
                Categorias
              </Form.Label>
              <Select
                isMulti
                options={categoryOptions}
                value={getSelectedOptions(form.categoryIds, categoryOptions)}
                onChange={handleCategoryChange}
                isDisabled={saving || loadingCatalogs}
                isLoading={loadingCatalogs}
                placeholder="Seleccione una o mas categorias"
                noOptionsMessage={() => "No hay categorias disponibles"}
                classNamePrefix="react-select"
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Se crean o eliminan relaciones product-categories segun la seleccion.
              </div>
            </Col>
            <Col md={6}>
              <Form.Label>
                <FaLayerGroup className="me-1" />
                Variaciones
              </Form.Label>
              <Select
                isMulti
                options={variationOptions}
                value={getSelectedOptions(form.variationIds, variationOptions)}
                onChange={handleVariationChange}
                isDisabled={saving || loadingCatalogs}
                isLoading={loadingCatalogs}
                placeholder="Seleccione una o mas variaciones"
                noOptionsMessage={() => "No hay variaciones disponibles"}
                classNamePrefix="react-select"
              />
              <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                Luego agregue valores para cada variacion desde la tabla.
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={saveProduct} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Guardando...
              </>
            ) : mode === "create" ? (
              "Crear"
            ) : (
              "Guardar"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!deleteProduct}
        onHide={() => {
          if (saving) return;
          setDeleteModalAlert(null);
          setDeleteProduct(null);
        }}
        centered
        backdrop={saving ? "static" : true}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Confirmar eliminacion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteModalAlert && (
            <Alert variant={deleteModalAlert.variant} onClose={() => setDeleteModalAlert(null)} dismissible>
              {deleteModalAlert.message}
            </Alert>
          )}
          Seguro que desea eliminar el producto <b>{deleteProduct?.name}</b>?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setDeleteModalAlert(null);
              setDeleteProduct(null);
            }}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={saving}>
            {saving ? "Eliminando..." : "Eliminar"}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showValuesModal}
        onHide={() => {
          if (saving) return;
          setValuesModalAlert(null);
          setPhotoSetForm(null);
          setShowValuesModal(false);
        }}
        centered
        size="xl"
        backdrop={saving ? "static" : true}
      >
        <Modal.Header closeButton={!saving}>
          <Modal.Title>Inventario por combinaciones: {valuesProduct?.name || "-"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {valuesModalAlert && (
            <Alert variant={valuesModalAlert.variant} onClose={() => setValuesModalAlert(null)} dismissible>
              {valuesModalAlert.message}
            </Alert>
          )}
          {!valuesProduct?.productVariations?.length ? (
            <Alert variant="info" className="mb-0">
              Este producto no tiene variaciones relacionadas. Agregue variaciones editando el producto.
            </Alert>
          ) : (
            <>
            <Row className="g-3">
              <Col lg={7}>
                <div className="table-responsive">
                  <Table bordered hover className="align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Variacion</th>
                        <th>Valores</th>
                        <th style={{ width: 130 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {valuesProduct.productVariations.map((variation) => {
                        const values = variation.values || [];
                        return (
                          <tr key={variation.id}>
                            <td>
                              <div className="fw-semibold">{getItemLabel(variation.variation)}</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                Relacion #{variation.id}
                              </div>
                            </td>
                            <td>
                              {values.length === 0 ? (
                                <span className="text-muted">Sin valores</span>
                              ) : (
                                <div className="d-flex flex-column gap-2">
                                  {values.map((value) => (
                                    <div
                                      key={value.id}
                                      className="d-flex justify-content-between align-items-center border rounded p-2"
                                    >
                                      <div>
                                        <div className="fw-semibold">
                                          {value.value || value.name || `Valor #${value.id}`}
                                        </div>
                                      </div>
                                      <div className="d-flex gap-1">
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          onClick={() => startValueEdit(variation.id, value)}
                                        >
                                          <FaEdit />
                                        </Button>
                                        <Button
                                          variant="outline-danger"
                                          className="btn-delete"
                                          size="sm"
                                          onClick={() => deleteVariationValue(value.id)}
                                        >
                                          <FaTrash />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td>
                              <Button variant="outline-secondary" size="sm" onClick={() => startValueCreate(variation.id)}>
                                <FaPlus className="me-1" />
                                Valor
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Col>
              <Col lg={5}>
                <Card className="border">
                  <Card.Body>
                    <h6 className="mb-3">{valueForm?.id ? "Editar valor" : "Nuevo valor"}</h6>
                    {!valueForm ? (
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        Seleccione "Valor" en una variacion o edite uno existente.
                      </div>
                    ) : (
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Label>Variacion</Form.Label>
                          <Form.Control
                            value={valueForm.name}
                            disabled
                          />
                          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                            La variacion base no se edita desde este valor para no afectar otros productos.
                          </div>
                        </Col>
                        <Col md={12}>
                          <Form.Label>Valor *</Form.Label>
                          <Form.Control
                            value={valueForm.value}
                            onChange={(event) =>
                              setValueForm((prev) => (prev ? { ...prev, value: event.target.value } : prev))
                            }
                            disabled={saving}
                          />
                        </Col>
                        <Col md={12} className="d-flex justify-content-end gap-2">
                          <Button variant="secondary" onClick={() => setValueForm(null)} disabled={saving}>
                            Cancelar
                          </Button>
                          <Button variant="primary" onClick={saveVariationValue} disabled={saving}>
                            {saving ? "Guardando..." : "Guardar valor"}
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Card className="border mt-3 product-combinations-card">
              <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
                  <div>
                    <h6 className="mb-1">Combinaciones de inventario</h6>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Seleccione Fotos en una combinacion para subir o editar sus imagenes.
                    </div>
                  </div>
                  <Button variant="outline-secondary" size="sm" onClick={startCombinationCreate}>
                    <FaPlus className="me-1" />
                    Combinacion
                  </Button>
                </div>

                <Row className="g-3">
                  <Col lg={7}>
                    <div className="table-responsive">
                      <Table bordered hover className="align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Combinacion</th>
                            <th style={{ width: 140 }}>Precio</th>
                            <th style={{ width: 120 }}>Disponible</th>
                            <th style={{ width: 230 }}>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!valuesProduct.productVariationCombinations?.length ? (
                            <tr>
                              <td colSpan={4} className="text-center py-4 text-muted">
                                No hay combinaciones creadas.
                              </td>
                            </tr>
                          ) : (
                            valuesProduct.productVariationCombinations.map((combination) => (
                              <tr key={combination.id}>
                                <td>
                                  <div className="fw-semibold">{getCombinationLabel(combination)}</div>
                                  <div className="text-muted" style={{ fontSize: 12 }}>
                                    Llave: {getCombinationCode(combination) || "-"}
                                  </div>
                                </td>
                                <td>{toMoney(combination.price)}</td>
                                <td>{combination.available ?? 0}</td>
                                <td>
                                  <div className="product-combination-actions">
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => startPhotoSetForCombination(combination)}
                                      title="Agregar o editar fotos"
                                      className="product-combination-photo-btn"
                                    >
                                      <FaImages className="me-1" />
                                      Fotos
                                    </Button>
                                    <Button variant="outline-primary" size="sm" onClick={() => startCombinationEdit(combination)}>
                                      <FaEdit />
                                    </Button>
                                    <Button
                                      variant="outline-danger"
                                      className="btn-delete"
                                      size="sm"
                                      onClick={() => deleteCombination(combination)}
                                    >
                                      <FaTrash />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </Col>

                  <Col lg={5}>
                    <Card className="border">
                      <Card.Body>
                        <h6 className="mb-3">{combinationForm?.id ? "Editar combinacion" : "Nueva combinacion"}</h6>
                        {!combinationForm ? (
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            Seleccione "Combinacion" para crear inventario con precio y disponibilidad.
                          </div>
                        ) : (
                          <Row className="g-3">
                            {valuesProduct.productVariations.map((variation) => {
                              const values = variation.values || [];
                              return (
                                <Col md={12} key={variation.id}>
                                  <Form.Label>{getItemLabel(variation.variation)} *</Form.Label>
                                  <Form.Select
                                    value={combinationForm.selectedValueIds[variation.id] || ""}
                                    onChange={(event) =>
                                      setCombinationForm((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              selectedValueIds: {
                                                ...prev.selectedValueIds,
                                                [variation.id]: Number(event.target.value),
                                              },
                                            }
                                          : prev
                                      )
                                    }
                                    disabled={saving}
                                  >
                                    <option value="">Seleccione un valor</option>
                                    {values.map((value) => (
                                      <option key={value.id} value={value.id}>
                                        {value.value || value.name || `Valor #${value.id}`}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Col>
                              );
                            })}

                            <Col md={12}>
                              <Form.Label>Var-code *</Form.Label>
                              <Form.Control
                                value={combinationForm.varCode}
                                onChange={(event) =>
                                  setCombinationForm((prev) => (prev ? { ...prev, varCode: event.target.value } : prev))
                                }
                                disabled={saving}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Precio *</Form.Label>
                              <Form.Control
                                type="number"
                                min={0}
                                step="0.01"
                                value={combinationForm.price}
                                onChange={(event) =>
                                  setCombinationForm((prev) => (prev ? { ...prev, price: event.target.value } : prev))
                                }
                                disabled={saving}
                              />
                            </Col>
                            <Col md={6}>
                              <Form.Label>Disponible *</Form.Label>
                              <Form.Control
                                type="number"
                                min={0}
                                value={combinationForm.available}
                                onChange={(event) =>
                                  setCombinationForm((prev) => (prev ? { ...prev, available: event.target.value } : prev))
                                }
                                disabled={saving}
                              />
                            </Col>
                            <Col md={12} className="d-flex justify-content-end gap-2">
                              <Button variant="secondary" onClick={() => setCombinationForm(null)} disabled={saving}>
                                Cancelar
                              </Button>
                              <Button variant="primary" onClick={saveCombination} disabled={saving}>
                                {saving ? "Guardando..." : "Guardar combinacion"}
                              </Button>
                            </Col>
                          </Row>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {photoSetForm && (
              <Card className="border mt-3 product-photo-set-card">
                <Card.Body>
                  <div className="product-photo-set-header">
                    <div>
                      <div className="product-photo-set-kicker">
                        <FaImages className="me-1" />
                        Imagenes del producto
                      </div>
                      <h6 className="mb-1">Fotos de la combinacion</h6>
                      <div className="product-photo-set-meta">
                        Aplica a {getPhotoSetScopeLabel(photoSetForm.selectedValueIds) || "-"}
                      </div>
                    </div>
                    <Button variant="outline-secondary" size="sm" onClick={() => setPhotoSetForm(null)} disabled={saving}>
                      Cerrar panel
                    </Button>
                  </div>

                  <Row className="g-3">
                    <Col lg={4}>
                      <Row className="g-3">
                        <Col md={12}>
                          <Form.Label>Estas fotos aplican a</Form.Label>
                          <div className="product-photo-scope-picker">
                            {photoSetForm.availableValueIds.map((valueId) => {
                              const match = getVariationValueById(valueId);
                              return (
                                <Form.Check
                                  key={valueId}
                                  type="checkbox"
                                  id={`photo-scope-${valueId}`}
                                  label={
                                    match
                                      ? `${match.variationLabel}: ${match.value.value || match.value.name || `Valor #${valueId}`}`
                                      : `Valor #${valueId}`
                                  }
                                  checked={photoSetForm.selectedValueIds.includes(valueId)}
                                  onChange={(event) => updatePhotoSetScope(valueId, event.target.checked)}
                                  disabled={saving}
                                />
                              );
                            })}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                            Marque solo Color para reutilizar las mismas fotos entre tallas.
                          </div>
                        </Col>
                        <Col md={12}>
                          <Form.Label>Nombre del set</Form.Label>
                          <Form.Control
                            value={photoSetForm.name}
                            onChange={(event) =>
                              setPhotoSetForm((prev) => (prev ? { ...prev, name: event.target.value } : prev))
                            }
                            disabled={saving}
                          />
                        </Col>
                        <Col md={6}>
                          <Form.Label>Prioridad</Form.Label>
                          <Form.Control
                            type="number"
                            value={photoSetForm.priority}
                            onChange={(event) =>
                              setPhotoSetForm((prev) => (prev ? { ...prev, priority: event.target.value } : prev))
                            }
                            disabled={saving}
                          />
                        </Col>
                        <Col md={6} className="d-flex align-items-end">
                          <Form.Check
                            type="switch"
                            label="Activo"
                            checked={photoSetForm.active}
                            onChange={(event) =>
                              setPhotoSetForm((prev) => (prev ? { ...prev, active: event.target.checked } : prev))
                            }
                            disabled={saving}
                          />
                        </Col>
                        <Col md={12}>
                          <Form.Label>Subir imagenes</Form.Label>
                          <div className="product-photo-upload">
                            <FaImages />
                            <div>
                              <div className="fw-semibold">Seleccione una o varias fotos</div>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                Se guardan en MinIO al presionar el boton principal.
                              </div>
                            </div>
                            <Form.Control
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={(event) => {
                                const input = event.currentTarget as HTMLInputElement;
                                setPhotoSetForm((prev) =>
                                  prev ? { ...prev, files: Array.from(input.files || []) } : prev
                                );
                              }}
                              disabled={saving}
                            />
                          </div>
                          {photoSetForm.files.length > 0 && (
                            <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                              {photoSetForm.files.length} archivo(s) listo(s) para subir.
                            </div>
                          )}
                        </Col>
                        <Col md={12} className="d-flex justify-content-end">
                          <Button variant="primary" onClick={savePhotoSet} disabled={saving}>
                            {saving ? "Guardando..." : photoSetForm.photoSetId ? "Guardar fotos" : "Crear set y subir"}
                          </Button>
                        </Col>
                      </Row>
                    </Col>

                    <Col lg={8}>
                      {!photoSetForm.photos.length ? (
                        <Alert variant="info" className="mb-0">
                          No hay imagenes guardadas para esta combinacion.
                        </Alert>
                      ) : (
                        <div className="product-photo-grid">
                          {photoSetForm.photos.map((photo) => (
                            <div className="product-photo-editor" key={photo.id}>
                              <div className="product-photo-preview">
                                {photo.url ? (
                                  <img src={photo.url} alt={photo.altText || photo.originalFilename || "Foto de producto"} />
                                ) : (
                                  <div className="product-photo-placeholder">
                                    <FaImages />
                                  </div>
                                )}
                              </div>
                              <div className="product-photo-fields">
                                <Form.Control
                                  size="sm"
                                  className="product-photo-alt-input"
                                  placeholder="Texto alternativo"
                                  value={photo.altText || ""}
                                  onChange={(event) => updatePhotoSetPhotoInForm(photo.id, { altText: event.target.value })}
                                  disabled={saving}
                                />
                                <div className="product-photo-meta-row">
                                  <div className="product-photo-position-field">
                                    <Form.Label>Orden</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={photo.position ?? 0}
                                      onChange={(event) =>
                                        updatePhotoSetPhotoInForm(photo.id, { position: Number(event.target.value) })
                                      }
                                      disabled={saving}
                                    />
                                  </div>
                                  <Form.Check
                                    type="radio"
                                    name={`primary-photo-${photoSetForm.photoSetId || "new"}`}
                                    label="Principal"
                                    className="product-photo-primary-check"
                                    checked={!!photo.primary}
                                    onChange={() =>
                                      setPhotoSetForm((prev) =>
                                        prev
                                          ? {
                                              ...prev,
                                              photos: prev.photos.map((item) => ({
                                                ...item,
                                                primary: item.id === photo.id,
                                              })),
                                            }
                                          : prev
                                      )
                                    }
                                    disabled={saving}
                                  />
                                </div>
                                <div className="product-photo-actions">
                                  <Button variant="outline-primary" size="sm" onClick={() => savePhotoMetadata(photo)} disabled={saving}>
                                    Guardar
                                  </Button>
                                  <Button
                                    variant="outline-danger"
                                    className="btn-delete"
                                    size="sm"
                                    onClick={() => deletePhoto(photo.id)}
                                    disabled={saving}
                                  >
                                    <FaTrash />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setValuesModalAlert(null);
              setShowValuesModal(false);
            }}
            disabled={saving}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Products;
